import { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, Download, X, Save, Star, Eye, EyeOff, Lock } from 'lucide-react';
import { statusLabel, statusColor, displayNames, type CoffeeSubmission, type CoffeeStatus } from '../lib/coffee-project';

const ADMIN_PASSWORD = 'bondcoffee2024';

const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/coffee-admin`;

async function adminFetch(path: string, options?: RequestInit) {
  const res = await fetch(`${FUNCTION_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${ADMIN_PASSWORD}`,
      ...(options?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error ?? `Request failed (${res.status})`);
  }
  return res.json();
}

const STATUSES: CoffeeStatus[] = ['waiting', 'scheduled', 'happened', 'well_see'];

export default function CoffeeAdmin() {
  const [authed, setAuthed] = useState(false);
  const [pw, setPw] = useState('');
  const [pwError, setPwError] = useState('');
  const [submissions, setSubmissions] = useState<CoffeeSubmission[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selected, setSelected] = useState<CoffeeSubmission | null>(null);
  const [editStatus, setEditStatus] = useState<CoffeeStatus>('waiting');
  const [editIsPublic, setEditIsPublic] = useState(true);
  const [editFeatured, setEditFeatured] = useState(false);
  const [editFeaturedStory, setEditFeaturedStory] = useState('');
  const [editPhotoUrl, setEditPhotoUrl] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminFetch('/');
      setSubmissions(data as CoffeeSubmission[]);
    } catch (err) {
      console.error('load error', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  const filtered = useMemo(() => {
    return submissions.filter((s) => {
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        const { label } = displayNames(s);
        return (
          label.toLowerCase().includes(q) ||
          s.relationship.toLowerCase().includes(q) ||
          (s.reason ?? '').toLowerCase().includes(q) ||
          String(s.project_number).includes(q)
        );
      }
      return true;
    });
  }, [submissions, search, statusFilter]);

  const handleLogin = () => {
    if (pw === ADMIN_PASSWORD) {
      setAuthed(true);
      setPwError('');
    } else {
      setPwError('Incorrect password');
    }
  };

  const openEdit = (s: CoffeeSubmission) => {
    setSelected(s);
    setEditStatus(s.status);
    setEditIsPublic(s.is_public);
    setEditFeatured(s.is_featured);
    setEditFeaturedStory(s.featured_story ?? '');
    setEditPhotoUrl(s.photo_url ?? '');
    setEditNotes(s.admin_notes ?? '');
    setSaveMsg('');
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    setSaveMsg('');
    try {
      const updated = await adminFetch(`/${selected.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          status: editStatus,
          is_public: editIsPublic,
          is_featured: editFeatured,
          featured_story: editFeaturedStory,
          photo_url: editPhotoUrl,
          admin_notes: editNotes,
          date_completed: selected.date_completed,
          date_scheduled: selected.date_scheduled,
        }),
      });
      setSaveMsg('Saved.');
      await load();
      setSelected(updated as CoffeeSubmission);
    } catch (err) {
      setSaveMsg('Error saving. Please try again.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const exportCsv = () => {
    const headers = ['project_number', 'submitter_name', 'other_name', 'relationship', 'time_since', 'reason', 'talk_about', 'contact_info', 'wants_help', 'permission_level', 'status', 'is_public', 'is_featured', 'date_submitted', 'date_completed'];
    const rows = submissions.map((s) =>
      headers.map((h) => {
        const val = (s as Record<string, unknown>)[h];
        const str = val == null ? '' : String(val).replace(/"/g, '""');
        return `"${str}"`;
      }).join(',')
    );
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'coffee-submissions.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0a0f18] flex items-center justify-center px-6">
        <div className="max-w-sm w-full space-y-6">
          <div className="text-center space-y-2">
            <Lock className="w-10 h-10 text-[#fda36b] mx-auto" />
            <h1 className="text-2xl font-bold text-white">Coffee Project Admin</h1>
          </div>
          <input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            placeholder="Password"
            className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:border-[#fda36b] focus:outline-none"
          />
          {pwError && <p className="text-rose-400 text-sm">{pwError}</p>}
          <button
            onClick={handleLogin}
            className="w-full bg-[#fda36b] text-[#132947] py-3 rounded-xl font-bold hover:bg-[#fda36b]/90 transition-all"
          >
            Enter
          </button>
          <a href="/coffee" className="block text-center text-white/40 text-sm hover:text-white/70">&larr; Back to project</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f18] text-white">
      <div className="border-b border-white/10 sticky top-0 bg-[#0a0f18]/95 backdrop-blur z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-xl font-bold">Coffee Project Admin</h1>
          <div className="flex items-center gap-4">
            <button
              onClick={exportCsv}
              className="inline-flex items-center gap-2 bg-white/5 border border-white/15 px-4 py-2 rounded-lg text-sm font-medium hover:bg-white/10 transition-all"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <a href="/coffee" className="text-white/40 hover:text-white/70 text-sm">View page →</a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatBox label="Total" value={submissions.length} />
          <StatBox label="Happened" value={submissions.filter((s) => s.status === 'happened').length} />
          <StatBox label="Waiting" value={submissions.filter((s) => s.status === 'waiting').length} />
          <StatBox label="Featured" value={submissions.filter((s) => s.is_featured).length} />
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, relationship, reason, number..."
              className="w-full bg-white/5 border border-white/15 rounded-xl pl-10 pr-4 py-2.5 text-white placeholder-white/40 focus:border-[#fda36b] focus:outline-none"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-white/5 border border-white/15 rounded-xl px-4 py-2.5 text-white focus:border-[#fda36b] focus:outline-none"
          >
            <option value="all">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{statusLabel(s)}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <p className="text-white/40 text-center py-20">Loading...</p>
        ) : filtered.length === 0 ? (
          <p className="text-white/40 text-center py-20">No submissions yet.</p>
        ) : (
          <div className="space-y-3">
            {filtered.map((s) => {
              const { label, isAnon } = displayNames(s);
              return (
                <button
                  key={s.id}
                  onClick={() => openEdit(s)}
                  className="w-full text-left bg-white/[0.03] border border-white/10 rounded-xl p-4 hover:border-[#fda36b]/30 transition-all flex items-center gap-4"
                >
                  <span className="text-white/30 text-xs font-mono w-16 flex-shrink-0">#{s.project_number}</span>
                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold truncate ${isAnon ? 'text-white/50' : 'text-white'}`}>{label}</p>
                    <p className="text-white/40 text-sm truncate">{s.relationship} · {s.time_since ?? '—'}</p>
                  </div>
                  {s.wants_help && (
                    <span className="hidden sm:inline-block text-[10px] font-bold uppercase text-[#fda36b] border border-[#fda36b]/40 rounded-full px-2 py-0.5">Wants Help</span>
                  )}
                  {!s.is_public && (
                    <EyeOff className="w-4 h-4 text-white/30 flex-shrink-0" />
                  )}
                  {s.is_featured && (
                    <Star className="w-4 h-4 text-[#fda36b] fill-[#fda36b] flex-shrink-0" />
                  )}
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border flex-shrink-0 ${statusColor(s.status)}`}>
                    {statusLabel(s.status)}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex justify-end" onClick={() => setSelected(null)}>
          <div
            className="bg-[#0d121c] border-l border-white/10 w-full max-w-md h-full overflow-y-auto p-6 space-y-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">Coffee #{selected.project_number}</h2>
              <button onClick={() => setSelected(null)} className="text-white/40 hover:text-white"><X className="w-6 h-6" /></button>
            </div>

            <div className="space-y-1">
              <p className="text-white font-semibold text-lg">{displayNames(selected).label}</p>
              <p className="text-white/50 text-sm">{selected.relationship} · {selected.time_since}</p>
              <p className="text-white/40 text-xs mt-2">Submitted {new Date(selected.date_submitted).toLocaleDateString()}</p>
            </div>

            {selected.reason && (
              <div>
                <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Reason</p>
                <p className="text-white/70 text-sm italic">"{selected.reason}"</p>
              </div>
            )}
            {selected.talk_about && (
              <div>
                <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Want to talk about</p>
                <p className="text-white/70 text-sm">{selected.talk_about}</p>
              </div>
            )}
            {selected.contact_info && (
              <div className="bg-[#fda36b]/10 border border-[#fda36b]/20 rounded-xl p-3">
                <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Contact (private)</p>
                <p className="text-[#fda36b] text-sm font-mono">{selected.contact_info}</p>
                <p className="text-white/40 text-xs mt-1">Wants help: {selected.wants_help ? 'Yes' : 'No'}</p>
              </div>
            )}

            <div className="space-y-4 border-t border-white/10 pt-6">
              <div>
                <label className="block text-white/60 text-sm font-medium mb-2">Status</label>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value as CoffeeStatus)}
                  className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-2.5 text-white focus:border-[#fda36b] focus:outline-none"
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>{statusLabel(s)}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => setEditIsPublic(!editIsPublic)}
                  className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    editIsPublic ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-white/5 text-white/50 border border-white/15'
                  }`}
                >
                  {editIsPublic ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  {editIsPublic ? 'Public' : 'Hidden'}
                </button>
                <button
                  onClick={() => setEditFeatured(!editFeatured)}
                  className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    editFeatured ? 'bg-[#fda36b]/15 text-[#fda36b] border border-[#fda36b]/40' : 'bg-white/5 text-white/50 border border-white/15'
                  }`}
                >
                  <Star className={`w-4 h-4 ${editFeatured ? 'fill-[#fda36b]' : ''}`} />
                  Featured
                </button>
              </div>

              <div>
                <label className="block text-white/60 text-sm font-medium mb-2">Featured story</label>
                <textarea
                  value={editFeaturedStory}
                  onChange={(e) => setEditFeaturedStory(e.target.value)}
                  placeholder="Longer story text for featured cards..."
                  className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:border-[#fda36b] focus:outline-none min-h-[100px] resize-none text-sm"
                />
              </div>

              <div>
                <label className="block text-white/60 text-sm font-medium mb-2">Photo URL</label>
                <input
                  value={editPhotoUrl}
                  onChange={(e) => setEditPhotoUrl(e.target.value)}
                  placeholder="https://..."
                  className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:border-[#fda36b] focus:outline-none text-sm"
                />
              </div>

              <div>
                <label className="block text-white/60 text-sm font-medium mb-2">Admin notes</label>
                <textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Internal notes..."
                  className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:border-[#fda36b] focus:outline-none min-h-[80px] resize-none text-sm"
                />
              </div>

              {saveMsg && <p className="text-emerald-400 text-sm">{saveMsg}</p>}

              <button
                onClick={save}
                disabled={saving}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#fda36b] text-[#132947] py-3 rounded-xl font-bold hover:bg-[#fda36b]/90 transition-all disabled:opacity-50"
              >
                <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
      <p className="text-3xl font-bold text-[#fda36b] tabular-nums">{value}</p>
      <p className="text-white/50 text-sm">{label}</p>
    </div>
  );
}
